import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { getLocale, setLocale, type Locale } from '@/lib/locale'
import { t } from '@/lib/i18n'
import { api, ApiError } from '@/lib/api'
import { useTokens, useCreateToken, useDeleteToken } from '@/hooks/useTokens'
import { Loading } from '@/components/ui/Loading'
import { ErrorState } from '@/components/ui/ErrorState'
import { EmptyState } from '@/components/ui/EmptyState'
import { Modal } from '@/components/ui/Modal'
import styles from './SettingsPage.module.css'

type Tab = 'general' | 'channels' | 'developer' | 'integrations'

const TABS: { key: Tab; label: string }[] = [
  { key: 'general', label: t('General') },
  { key: 'channels', label: t('Notification channels') },
  { key: 'developer', label: t('Developer') },
  { key: 'integrations', label: t('Integrations') },
]

export default function SettingsPage() {
  const [tab, setTab] = useState<Tab>('general')
  const [locale, setLocaleState] = useState<Locale>(getLocale())

  function handleLocaleChange(value: string) {
    const next = value === 'en' ? 'en' : 'ko'
    setLocale(next)
    setLocaleState(next)
  }

  return (
    <>
      <div className={styles.tabBar}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`${styles.tabBtn} ${tab === t.key ? styles.tabActive : ''}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>언어 및 지역</h2>
          <div className={styles.card}>
            <label className={styles.label} htmlFor="locale-lang">로케일</label>
            <select
              id="locale-lang"
              className={styles.select}
              value={locale}
              onChange={(e) => handleLocaleChange(e.target.value)}
            >
              <option value="ko">한국어 (ko-KR)</option>
              <option value="en">English (en-US)</option>
            </select>
            <p className={styles.hint}>
              현재 적용됨: <code>{locale}</code> — 변경 즉시 <code>&lt;html lang&gt;</code>에 반영됩니다.
            </p>
          </div>
        </div>
      )}

      {tab === 'channels' && <ChannelsTab />}

      {tab === 'developer' && <DeveloperTab />}

      {tab === 'integrations' && <IntegrationsTab />}
    </>
  )
}

function DeveloperTab() {
  const { data: tokens, isLoading, error, refetch } = useTokens()
  const createToken = useCreateToken()
  const deleteToken = useDeleteToken()

  const [modalOpen, setModalOpen] = useState(false)
  const [tokenName, setTokenName] = useState('')
  const [createdToken, setCreatedToken] = useState<string | null>(null)

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    createToken.mutate({ name: tokenName, scope: ['*'] }, {
      onSuccess: (res) => {
        setCreatedToken(res.data.token)
        setTokenName('')
        setModalOpen(false)
      },
    })
  }

  function handleDelete(id: string) {
    deleteToken.mutate(id)
  }

  return (
    <div className={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
        <h2 className={styles.sectionTitle} style={{ marginBottom: 0 }}>API 토큰</h2>
        <button
          type="button"
          onClick={() => { setCreatedToken(null); setModalOpen(true) }}
          style={{
            padding: '8px 16px', background: 'var(--color-primary)', color: '#fff',
            borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13,
          }}
        >
          + 토큰 생성
        </button>
      </div>

      {/* Newly created token — shown once */}
      {createdToken && (
        <div style={{
          background: 'color-mix(in srgb, var(--color-revenue) 8%, var(--color-surface))',
          border: '1px solid color-mix(in srgb, var(--color-revenue) 20%, var(--color-border))',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-md)',
          marginBottom: 'var(--space-md)',
        }}>
          <p style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-revenue-text)', marginBottom: 'var(--space-xs)' }}>
            ✅ 토큰이 생성되었습니다 — 지금만 복사할 수 있습니다.
          </p>
          <code style={{
            display: 'block', fontFamily: 'var(--font-mono)', fontSize: 13,
            background: 'var(--color-surface-alt)', padding: 'var(--space-sm) var(--space-md)',
            borderRadius: 'var(--radius-sm)', wordBreak: 'break-all', userSelect: 'all',
          }}>
            {createdToken}
          </code>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(createdToken)}
            style={{ marginTop: 'var(--space-xs)', fontSize: 12, color: 'var(--color-accent)', fontWeight: 600 }}
          >
            📋 클립보드에 복사
          </button>
        </div>
      )}

      {/* Token list */}
      {isLoading && <Loading variant="rows" count={3} />}
      {error && <ErrorState error={error} onRetry={() => refetch()} />}
      {tokens && tokens.length === 0 && (
        <EmptyState title="발급된 토큰이 없습니다" description="각 프로젝트(X-Auto 등)가 데이터를 푸시하려면 토큰이 필요합니다." />
      )}
      {tokens && tokens.length > 0 && (
        <div className={styles.card} style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-surface-alt)' }}>
                <th style={{ textAlign: 'left', padding: 'var(--space-sm) var(--space-md)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary)' }}>이름</th>
                <th style={{ textAlign: 'left', padding: 'var(--space-sm) var(--space-md)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary)' }}>토큰</th>
                <th style={{ textAlign: 'left', padding: 'var(--space-sm) var(--space-md)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary)' }}>마지막 사용</th>
                <th style={{ textAlign: 'right', padding: 'var(--space-sm) var(--space-md)' }}></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: 'var(--space-md)', fontWeight: 600 }}>{t.name}</td>
                  <td style={{ padding: 'var(--space-md)', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-tertiary)' }}>{t.masked}</td>
                  <td style={{ padding: 'var(--space-md)', fontSize: 12, color: 'var(--color-text-tertiary)' }}>{t.lastUsedAt ?? '—'}</td>
                  <td style={{ padding: 'var(--space-md)', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => handleDelete(t.id)}
                      style={{ fontSize: 12, color: 'var(--color-danger-text)', fontWeight: 500 }}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Webhook endpoints info */}
      <h2 className={styles.sectionTitle} style={{ marginTop: 'var(--space-xl)' }}>웹훅 엔드포인트</h2>
      <div className={styles.card}>
        <pre className={styles.codeBlock}>
          {`POST /api/v1/ingest/ai-usage    ← 프로젝트가 AI 비용 푸시 (Bearer 토큰)
POST /api/v1/ingest/metric      ← 범용 메트릭 푸시 (Bearer 토큰)
POST /api/v1/heartbeats/:token  ← 로컬 서비스 heartbeat (토큰 없음)
POST /webhooks/stripe           ← Stripe 결제 웹훅 (서명 검증)`}
        </pre>
        <p className={styles.hint}>
          ingest 엔드포인트는 <code>Authorization: Bearer quay_...</code> 헤더가 필요합니다.
        </p>
      </div>

      {/* Create token modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="토큰 생성">
        <form onSubmit={handleCreate}>
          <label className={styles.label} htmlFor="token-name">토큰 이름</label>
          <input
            id="token-name"
            className={styles.input}
            type="text"
            placeholder="예: X-Auto ingest"
            value={tokenName}
            onChange={(e) => setTokenName(e.target.value)}
            required
          />
          <p className={styles.hint} style={{ marginTop: 'var(--space-sm)' }}>
            이 토큰으로 모든 프로젝트에 데이터를 푸시할 수 있습니다 (scope: *).
          </p>
          <button
            type="submit"
            disabled={createToken.isPending}
            style={{
              marginTop: 24, width: '100%', height: 44,
              background: 'var(--color-primary)', color: '#fff',
              borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14,
              opacity: createToken.isPending ? 0.6 : 1,
            }}
          >
            {createToken.isPending ? '생성 중...' : '토큰 생성'}
          </button>
        </form>
      </Modal>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// ChannelsTab — alert/notification channel management
// ═══════════════════════════════════════════════════════════════════

type ChannelType = 'email' | 'slack' | 'discord' | 'telegram' | 'webhook'

const CHANNEL_TYPES: { value: ChannelType; label: string; icon: string }[] = [
  { value: 'slack', label: 'Slack', icon: '💬' },
  { value: 'discord', label: 'Discord', icon: '🎮' },
  { value: 'telegram', label: 'Telegram', icon: '✈️' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'webhook', label: 'Webhook', icon: '🔗' },
]

interface Channel {
  id: string
  type: string
  name: string
  configJson: string
  enabled: boolean
  createdAt: string
}

function ChannelsTab() {
  const qc = useQueryClient()
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.channels.list(),
  })
  const channels = data?.data ?? []

  const createChannel = useMutation({
    mutationFn: (body: { type: string; name: string; configJson?: string }) =>
      api.channels.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  })

  const deleteChannel = useMutation({
    mutationFn: (id: string) => api.channels.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channels'] }),
  })

  const [modalOpen, setModalOpen] = useState(false)
  const [name, setName] = useState('')
  const [type, setType] = useState<ChannelType>('slack')
  const [endpoint, setEndpoint] = useState('')

  const typeMeta = (t: string) =>
    CHANNEL_TYPES.find((ct) => ct.value === t) ?? { label: t, icon: '🔔' }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    const configJson =
      type === 'email'
        ? JSON.stringify({ email: endpoint })
        : JSON.stringify({ url: endpoint })
    createChannel.mutate(
      { type, name, configJson },
      {
        onSuccess: () => {
          setName('')
          setEndpoint('')
          setType('slack')
          setModalOpen(false)
        },
      },
    )
  }

  function handleDelete(id: string) {
    deleteChannel.mutate(id)
  }

  return (
    <div className={styles.section}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
        <h2 className={styles.sectionTitle} style={{ marginBottom: 0 }}>알림 채널</h2>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          style={{
            padding: '8px 16px', background: 'var(--color-primary)', color: '#fff',
            borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13,
          }}
        >
          + 채널 추가
        </button>
      </div>

      {isLoading && <Loading variant="rows" count={2} />}
      {error && <ErrorState error={error} onRetry={() => refetch()} />}
      {channels.length === 0 && !isLoading && !error && (
        <EmptyState
          title="등록된 알림 채널이 없습니다"
          description="Slack, Discord, Telegram, Email 또는 Webhook 채널을 추가하여 알림을 받으세요."
        />
      )}
      {channels.length > 0 && (
        <div className={styles.card} style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-surface-alt)' }}>
                <th style={{ textAlign: 'left', padding: 'var(--space-sm) var(--space-md)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary)' }}>채널</th>
                <th style={{ textAlign: 'left', padding: 'var(--space-sm) var(--space-md)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary)' }}>유형</th>
                <th style={{ textAlign: 'left', padding: 'var(--space-sm) var(--space-md)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary)' }}>상태</th>
                <th style={{ textAlign: 'right', padding: 'var(--space-sm) var(--space-md)' }}></th>
              </tr>
            </thead>
            <tbody>
              {channels.map((ch: Channel) => {
                const meta = typeMeta(ch.type)
                return (
                  <tr key={ch.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: 'var(--space-md)', fontWeight: 600 }}>
                      <span style={{ marginRight: 'var(--space-xs)' }}>{meta.icon}</span>
                      {ch.name}
                    </td>
                    <td style={{ padding: 'var(--space-md)', fontSize: 13, color: 'var(--color-text-secondary)' }}>
                      {meta.label}
                    </td>
                    <td style={{ padding: 'var(--space-md)', fontSize: 12 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 11,
                        fontWeight: 600,
                        background: ch.enabled
                          ? 'color-mix(in srgb, var(--color-revenue) 12%, transparent)'
                          : 'var(--color-surface-alt)',
                        color: ch.enabled
                          ? 'var(--color-revenue-text)'
                          : 'var(--color-text-tertiary)',
                      }}>
                        {ch.enabled ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td style={{ padding: 'var(--space-md)', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={() => handleDelete(ch.id)}
                        style={{ fontSize: 12, color: 'var(--color-danger-text)', fontWeight: 500 }}
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create channel modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="알림 채널 추가">
        <form onSubmit={handleCreate}>
          <label className={styles.label} htmlFor="ch-name">채널 이름</label>
          <input
            id="ch-name"
            className={styles.input}
            type="text"
            placeholder="예: 개발팀 Slack"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <label className={styles.label} htmlFor="ch-type">유형</label>
          <select
            id="ch-type"
            className={styles.select}
            value={type}
            onChange={(e) => setType(e.target.value as ChannelType)}
          >
            {CHANNEL_TYPES.map((ct) => (
              <option key={ct.value} value={ct.value}>{ct.icon} {ct.label}</option>
            ))}
          </select>

          <label className={styles.label} htmlFor="ch-endpoint">
            {type === 'email' ? '이메일 주소' : 'Webhook URL'}
          </label>
          <input
            id="ch-endpoint"
            className={styles.input}
            type={type === 'email' ? 'email' : 'url'}
            placeholder={type === 'email' ? 'alerts@example.com' : 'https://hooks.slack.com/services/...'}
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            required
          />

          <p className={styles.hint} style={{ marginTop: 'var(--space-sm)' }}>
            {type === 'email'
              ? '알림이 지정된 이메일 주소로 발송됩니다.'
              : '알림이 이 URL로 POST 요청으로 전송됩니다.'}
          </p>

          <button
            type="submit"
            disabled={createChannel.isPending}
            style={{
              marginTop: 24, width: '100%', height: 44,
              background: 'var(--color-primary)', color: '#fff',
              borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14,
              opacity: createChannel.isPending ? 0.6 : 1,
            }}
          >
            {createChannel.isPending ? '추가 중...' : '채널 추가'}
          </button>
        </form>
      </Modal>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════
// IntegrationsTab — third-party integration config (Cloudflare, …)
// ═══════════════════════════════════════════════════════════════════

function IntegrationsTab() {
  const qc = useQueryClient()
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.integrations.get(),
  })

  const cf = data?.data?.cloudflare
  const tokenConfigured = !!cf?.apiTokenConfigured
  const maskedToken =
    typeof cf?.apiToken === 'string' && cf.apiToken.length > 0 ? cf.apiToken : ''

  const sp = data?.data?.stripe
  const stripeKeyConfigured = !!sp?.secretKeyConfigured
  const maskedStripeKey =
    typeof sp?.secretKey === 'string' && sp.secretKey.length > 0 ? sp.secretKey : ''

  const [token, setToken] = useState('')
  const [stripeKey, setStripeKey] = useState('')
  const [savedAt, setSavedAt] = useState<number | null>(null)

  const save = useMutation({
    mutationFn: (body: Record<string, Record<string, unknown>>) => api.integrations.update(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] })
      setToken('')
      setSavedAt(Date.now())
    },
  })

  const mutationError =
    save.error instanceof ApiError ? save.error : null

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = token.trim()
    if (!trimmed) return
    save.mutate({ cloudflare: { apiToken: trimmed } })
  }

  function handleSaveStripe(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = stripeKey.trim()
    if (!trimmed) return
    save.mutate({ stripe: { secretKey: trimmed } })
  }

  // Stripe sync
  const syncStripe = useMutation({
    mutationFn: () => api.stripe.sync(30),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stripe-status'] })
    },
  })

  const { data: stripeStatus } = useQuery({
    queryKey: ['stripe-status'],
    queryFn: () => api.stripe.status(),
    enabled: stripeKeyConfigured,
  })

  if (isLoading) return <Loading variant="block" />
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />

  const recentlySaved = savedAt !== null && Date.now() - savedAt < 4000

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Cloudflare Analytics</h2>
      <div className={styles.card}>
        <label className={styles.label} htmlFor="cf-api-token">API Token</label>

        {tokenConfigured && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
            padding: '6px 10px', borderRadius: 'var(--radius-sm)',
            background: 'color-mix(in srgb, var(--color-revenue) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-revenue) 20%, var(--color-border))',
            fontSize: 13, marginBottom: 'var(--space-sm)',
          }}>
            <span style={{
              fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)',
              flex: 1, wordBreak: 'break-all',
            }}>
              {maskedToken}
            </span>
            <span style={{
              padding: '2px 8px', borderRadius: 'var(--radius-full)',
              fontSize: 11, fontWeight: 700,
              background: 'color-mix(in srgb, var(--color-revenue) 16%, transparent)',
              color: 'var(--color-revenue-text)',
            }}>
              Configured
            </span>
          </div>
        )}

        <form onSubmit={handleSave}>
          <input
            id="cf-api-token"
            className={styles.input}
            type="password"
            placeholder={tokenConfigured ? '새 토큰으로 교체하려면 입력' : 'Cloudflare API 토큰 입력'}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            autoComplete="off"
          />

          <p className={styles.hint} style={{ marginTop: 'var(--space-sm)' }}>
            <a
              href="https://dash.cloudflare.com/profile/api-tokens"
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--color-accent)', fontWeight: 600 }}
            >
              Cloudflare 대시보드
            </a>
            {' '}에서 토큰 생성 → <strong>Account → Workers Analytics → Read</strong> 권한이 필요합니다.
          </p>

          {mutationError && (
            <p style={{ fontSize: 13, color: 'var(--color-danger-text)', marginTop: 'var(--space-sm)' }}>
              저장 실패: {mutationError.message}
            </p>
          )}
          {recentlySaved && (
            <p style={{ fontSize: 13, color: 'var(--color-revenue-text)', marginTop: 'var(--space-sm)' }}>
              ✅ 저장되었습니다.
            </p>
          )}

          <button
            type="submit"
            disabled={save.isPending || token.trim().length === 0}
            style={{
              marginTop: 'var(--space-md)', height: 40, padding: '0 20px',
              background: 'var(--color-primary)', color: '#fff',
              borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14,
              opacity: save.isPending || token.trim().length === 0 ? 0.6 : 1,
            }}
          >
            {save.isPending ? '저장 중...' : '저장'}
          </button>
        </form>
      </div>

      {/* ─── Stripe ─── */}
      <h2 className={styles.sectionTitle} style={{ marginTop: 'var(--space-xl)' }}>Stripe</h2>
      <div className={styles.card}>
        <label className={styles.label} htmlFor="stripe-key">Secret Key</label>

        {stripeKeyConfigured && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
            padding: '6px 10px', borderRadius: 'var(--radius-sm)',
            background: 'color-mix(in srgb, var(--color-revenue) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-revenue) 20%, var(--color-border))',
            fontSize: 13, marginBottom: 'var(--space-sm)',
          }}>
            <span style={{
              fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)',
              flex: 1, wordBreak: 'break-all',
            }}>
              {maskedStripeKey}
            </span>
            <span style={{
              padding: '2px 8px', borderRadius: 'var(--radius-full)',
              fontSize: 11, fontWeight: 700,
              background: 'color-mix(in srgb, var(--color-revenue) 16%, transparent)',
              color: 'var(--color-revenue-text)',
            }}>
              Configured
            </span>
          </div>
        )}

        <form onSubmit={handleSaveStripe}>
          <input
            id="stripe-key"
            className={styles.input}
            type="password"
            placeholder={stripeKeyConfigured ? '새 키로 교체하려면 입력' : 'Stripe Secret Key 입력 (sk_...)'}
            value={stripeKey}
            onChange={(e) => setStripeKey(e.target.value)}
            autoComplete="off"
          />

          <p className={styles.hint} style={{ marginTop: 'var(--space-sm)' }}>
            <a
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noreferrer"
              style={{ color: 'var(--color-accent)', fontWeight: 600 }}
            >
              Stripe 대시보드
            </a>
            {' '}에서 Secret Key 복사 · 결제 내역을 ledger에 동기화합니다.
          </p>

          <button
            type="submit"
            disabled={save.isPending || stripeKey.trim().length === 0}
            style={{
              marginTop: 'var(--space-md)', height: 40, padding: '0 20px',
              background: 'var(--color-primary)', color: '#fff',
              borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 14,
              opacity: save.isPending || stripeKey.trim().length === 0 ? 0.6 : 1,
            }}
          >
            {save.isPending ? '저장 중...' : '저장'}
          </button>
        </form>

        {/* Sync section */}
        {stripeKeyConfigured && (
          <div style={{ marginTop: 'var(--space-lg)', paddingTop: 'var(--space-md)', borderTop: '1px solid var(--color-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>데이터 동기화</div>
                {stripeStatus?.data && (
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    {stripeStatus.data.lastSyncAt
                      ? `마지막 동기화: ${new Date(stripeStatus.data.lastSyncAt).toLocaleString('ko-KR')}`
                      : '아직 동기화되지 않음'}
                    {stripeStatus.data.ledgerEntries > 0 && ` · ${stripeStatus.data.ledgerEntries}개 레코드`}
                  </div>
                )}
              </div>
              <button
                onClick={() => syncStripe.mutate()}
                disabled={syncStripe.isPending}
                style={{
                  height: 36, padding: '0 16px',
                  background: 'var(--color-accent)', color: '#fff',
                  borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: 13,
                  opacity: syncStripe.isPending ? 0.6 : 1, whiteSpace: 'nowrap',
                }}
              >
                {syncStripe.isPending ? '동기화 중...' : '지금 동기화'}
              </button>
            </div>

            {syncStripe.data && (
              <div style={{
                marginTop: 'var(--space-sm)', padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                background: 'color-mix(in srgb, var(--color-revenue) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--color-revenue) 16%, var(--color-border))',
                fontSize: 13,
              }}>
                ✅ 동기화 완료: 결제 {syncStripe.data.data.charges.inserted}건 ·
                구독 {syncStripe.data.data.invoices.inserted}건 ·
                환불 {syncStripe.data.data.refunds.inserted}건 추가
              </div>
            )}
            {syncStripe.error instanceof ApiError && (
              <p style={{ fontSize: 13, color: 'var(--color-danger-text)', marginTop: 'var(--space-sm)' }}>
                동기화 실패: {syncStripe.error.message}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
